import type {
  GameContext,
  GameInstance,
  GameModule,
  PreloadContext,
} from '@arcraz/arcadia-types';
import { CubeJumpInstance } from './instance.js';
import { PALETTE_HINT } from './palette.js';

/**
 * CUBE JUMP — twelfth cartridge (sc-3948).
 *
 * Q*bert-style isometric puzzle action. Coffee-bean player hops between
 * cube tops on a 7-row pyramid (28 cubes total), painting tops as it
 * lands. Snake chases, red balls fall. Off-edge = death. 3 lives.
 *
 * Original mechanic: between levels the pyramid rotates 90° so the
 * safe edges shift — memory + reflex.
 *
 * MVP build is a 6-step sequence (revised plan: each step ships
 * mechanic AND texture so the cartridge never feels "MVP-thin"):
 *
 *   1. Skeleton + paint + level rotation — pyramid renders, bean
 *      idle-wiggles, 4-dir jump, paint flash, level-clear card with
 *      90° rotation, attract-mode demo loop. **(this commit)**
 *   2. Falls + lives + game-over — off-edge fall, screen shake,
 *      CRT-glitch game-over.
 *   3. Snake enemy — DANGER! card, hiss SFX, grid-chase AI, kill
 *      cinematic.
 *   4. Red balls — warning flash, bounce SFX, off-pyramid tracker dots.
 *   5. Round structure — PYRAMID ALPHA (1-5), PYRAMID BETA (6-10)
 *      palette shift, GET READY card, round bonus.
 *   6. Polish + determinism — ambient music bed, in-cabinet TOP 5,
 *      initials roll, TREASURE! win celebration, 1000-run determinism
 *      test, V010 migration, registry entry.
 *
 * Resolution: 480×270 pixelated. Pyramid sits in a centered ~280-wide
 * playfield column with cartridge-owned HUD chrome (CUBE JUMP wordmark
 * + lives pips) in the gutters — landscape-only rule, not black bars.
 *
 * Palette: coffee-shop tones filtered through vaporwave-CRT. Cream
 * unpainted → neon-cyan painted (the "coffee with froth" read). Bean
 * is hot-orange to pop against the cool cubes.
 */
const module_: GameModule = {
  id: 'cube-jump',
  title: 'CUBE JUMP',
  manufacturer: 'ARCADIA AMUSEMENTS',
  year: 1984,
  genre: 'arena',
  resolution: { w: 480, h: 270 },
  pixelated: true,
  paletteHint: PALETTE_HINT,
  validatorVersion: 1,
  controls: [
    { keys: 'UP+RIGHT', action: 'HOP NE' },
    { keys: 'UP+LEFT', action: 'HOP NW' },
    { keys: 'DOWN+RIGHT', action: 'HOP SE' },
    { keys: 'DOWN+LEFT', action: 'HOP SW' },
    { keys: 'Z', action: 'RESTART (AT GAME OVER)' },
  ],

  async preload(ctx: PreloadContext): Promise<void> {
    // Hop — short triangle blip on every move. Quiet so rapid hops don't
    // fatigue the ear; quick decay so each hop sounds discrete rather
    // than running into the next.
    ctx.registerSfx('hop', {
      wave: 'triangle',
      pitch: [
        { t: 0, f: 660 },
        { t: 24, f: 880 },
      ],
      gain: [
        { t: 0, g: 0 },
        { t: 3, g: 0.14 },
        { t: 24, g: 0.0 },
      ],
    });
    // Paint — small "click" when a cube is freshly painted. Slightly
    // brighter than hop so the player gets feedback "I painted this
    // one" distinct from "I hopped onto a cube I already painted."
    ctx.registerSfx('paint', {
      wave: 'square',
      pitch: [
        { t: 0, f: 880 },
        { t: 20, f: 1320 },
      ],
      gain: [
        { t: 0, g: 0 },
        { t: 3, g: 0.18 },
        { t: 20, g: 0.0 },
      ],
    });
    // Level clear — ascending triad fanfare. The "pyramid clear!"
    // payoff sting that plays the moment the final cube paints.
    ctx.registerSfx('level-clear', {
      wave: 'square',
      pitch: [
        { t: 0, f: 523 },
        { t: 80, f: 659 },
        { t: 160, f: 784 },
        { t: 240, f: 1046 },
      ],
      gain: [
        { t: 0, g: 0 },
        { t: 6, g: 0.28 },
        { t: 240, g: 0.0 },
      ],
    });
    // Whoa — falling-bean cry. Descending sine wail with a quick
    // amplitude flare so the player reads "I'm falling, that's a
    // mistake!" without it being so loud it fatigues.
    ctx.registerSfx('whoa', {
      wave: 'sine',
      pitch: [
        { t: 0, f: 520 },
        { t: 60, f: 180 },
        { t: 200, f: 90 },
      ],
      gain: [
        { t: 0, g: 0 },
        { t: 6, g: 0.32 },
        { t: 200, g: 0.0 },
      ],
    });
    // Hiss — snake-spawn warning. Noise burst with a slight pitch
    // drop, like a kettle. Fires once on the DANGER! card so the
    // player's ears catch it even if they're looking elsewhere on
    // the screen.
    ctx.registerSfx('hiss', {
      wave: 'noise',
      pitch: [
        { t: 0, f: 220 },
        { t: 180, f: 140 },
      ],
      gain: [
        { t: 0, g: 0 },
        { t: 12, g: 0.34 },
        { t: 180, g: 0.0 },
      ],
    });
    // Snake hop — lower-pitched than the bean's hop, with a slithery
    // "scale" character. Plays on every snake hop. Pitched/voiced
    // distinct enough that the player can tell snake-hop from
    // bean-hop with eyes closed.
    ctx.registerSfx('snake-hop', {
      wave: 'sawtooth',
      pitch: [
        { t: 0, f: 220 },
        { t: 28, f: 180 },
      ],
      gain: [
        { t: 0, g: 0 },
        { t: 3, g: 0.12 },
        { t: 28, g: 0.0 },
      ],
    });
    // Chomp — kill SFX when the snake catches the bean. Short noise
    // crunch with sub-bass thump; matches the "predator wins" beat.
    ctx.registerSfx('chomp', {
      wave: 'noise',
      pitch: [
        { t: 0, f: 240 },
        { t: 80, f: 80 },
      ],
      gain: [
        { t: 0, g: 0 },
        { t: 4, g: 0.45 },
        { t: 80, g: 0.0 },
      ],
    });
    // Ball bounce — chip-style "tic" on each ball hop. Higher pitch
    // than the bean's hop SFX and shorter than snake-hop so all three
    // hop sounds are distinguishable on the same frame.
    ctx.registerSfx('ball-bounce', {
      wave: 'square',
      pitch: [
        { t: 0, f: 880 },
        { t: 14, f: 660 },
      ],
      gain: [
        { t: 0, g: 0 },
        { t: 2, g: 0.10 },
        { t: 14, g: 0.0 },
      ],
    });
    // Ball warning — short, mid-pitched sting fired the moment the
    // pre-spawn warning flash appears at the apex. Gives the player
    // an audio cue to look up at the pyramid top.
    ctx.registerSfx('ball-warning', {
      wave: 'triangle',
      pitch: [
        { t: 0, f: 440 },
        { t: 24, f: 660 },
      ],
      gain: [
        { t: 0, g: 0 },
        { t: 3, g: 0.16 },
        { t: 24, g: 0.0 },
      ],
    });
    // Ball hit — distinct from chomp so the player can tell whether
    // a snake or a ball killed them. Sharper percussive crunch.
    ctx.registerSfx('ball-hit', {
      wave: 'noise',
      pitch: [
        { t: 0, f: 360 },
        { t: 60, f: 120 },
      ],
      gain: [
        { t: 0, g: 0 },
        { t: 3, g: 0.40 },
        { t: 60, g: 0.0 },
      ],
    });
  },

  init(ctx: GameContext): GameInstance {
    return new CubeJumpInstance(ctx);
  },
};

export default module_;
